import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import './ProductDetail.css';
import { useFavorites } from '../../context/FavoritesContext';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import pb from '../../services/pocketbase';
import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';
import Select from 'react-select';
import imageCompression from 'browser-image-compression';

function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();

  const [product, setProduct] = useState(null);
  const [similarProducts, setSimilarProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const { favorites, toggleFavorites } = useFavorites();
  const { addToCart } = useCart();
  const { user } = useAuth();

  const [activeImage, setActiveImage] = useState('');
  const [openLightbox, setOpenLightbox] = useState(false);
  const [imageIndex, setImageIndex] = useState(0); 
  const [isEditing, setIsEditing] = useState(false);
  const [editedProduct, setEditedProduct] = useState({});
  const [categories, setCategories] = useState([]);
  const [colors, setColors] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [pearlTypes, setPearlTypes] = useState([]);
  const [newImages, setNewImages] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const fetchProduct = async () => {
      console.log(`Tentative de récupération du produit avec le slug : "${slug}"`);
      setLoading(true);
      setError(null);
      try {
        // Récupérer les données de référence pour l'édition
        const [currentProduct, categoriesData, colorsData, materialsData, pearlTypesData] = await Promise.all([
          pb.collection('products').getFirstListItem(`slug = "${slug}"`, {
            expand: 'category,color,material,pearl_type'
          }),
          pb.collection('categories').getFullList(),
          pb.collection('colors').getFullList(),
          pb.collection('materials').getFullList(),
          pb.collection('pearl_types').getFullList()
        ]);

        if (!isMounted) return;

        console.log("Produit trouvé :", currentProduct);
        setProduct(currentProduct);
        setCategories(categoriesData);
        setColors(colorsData);
        setMaterials(materialsData);
        setPearlTypes(pearlTypesData);

        if (currentProduct.image && currentProduct.image.length > 0) {
          const imageUrl = pb.files.getURL(currentProduct, currentProduct.image[0]);
          setActiveImage(imageUrl);
        }

        if (currentProduct.category && currentProduct.category.length > 0) {
          const categoryFilters = currentProduct.category.map(catId => `category.id = "${catId}"`).join(' || ');
          const resultList = await pb.collection('products').getList(1, 5, {
            filter: `(${categoryFilters}) && id != "${currentProduct.id}"`
          });

          if (!isMounted) return;

          setSimilarProducts(resultList.items);
        }

      } catch (err) {
        console.error("Erreur détaillée lors de la récupération du produit:", err);
        if (!isMounted) return;
        if (err.status === 404) {
          setError(`Le produit avec le slug "${slug}" n'a pas été trouvé.`);
        } else {
          setError("Une erreur est survenue lors du chargement du produit.");
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchProduct();

    return () => {
      isMounted = false;
    };
  }, [slug]);

  if (loading) {
    return <div className="loading-message">Chargement du produit...</div>;
  }

  if (error) {
    return (
      <div className="product-not-found">
        <h2>Oups ! {error}</h2>
        <Link to="/catalogue">Retour au catalogue</Link>
      </div>
    );
  }

  if (!product) {
    // Ce cas est normal pendant le chargement ou si une erreur s'est produite.
    // La logique de chargement et d'erreur ci-dessus gère déjà ces états.
    return null;
  }

  const isFavorited = favorites.some(item => item.id === product.id);

  const handleImageClick = (index) => {
    setImageIndex(index);
    setOpenLightbox(true);
  };

  // Utiliser les images mises à jour en temps réel pendant l'édition
  const currentImages = isEditing ? (product.image || []) : (product.image || []);
  const productImages = currentImages.map(img => ({ src: pb.files.getURL(product, img) }));

  // Vérifier si l'utilisateur est admin
  const isAdmin = user && user.role === 'admin';

  // Fonctions d'édition pour les admins
  const startEditing = () => {
    setIsEditing(true);
    setEditedProduct({
      name: product.name,
      price: product.price,
      description: product.description,
      stock: product.stock,
      is_unique: product.is_unique,
      is_sold: product.is_sold,
      category: product.category || [],
      color: product.color || [],
      material: product.material || [],
      pearl_type: product.pearl_type || []
    });
    setNewImages([]);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setEditedProduct(null);
    setNewImages([]);
  };

  const saveProduct = async () => {
    try {
      // Utiliser la même logique que ProductManager avec FormData
      const formData = new FormData();
      
      // Ajouter les métadonnées
      formData.append('name', editedProduct.name);
      formData.append('description', editedProduct.description);
      formData.append('price', String(editedProduct.price || 0));
      formData.append('stock', String(editedProduct.is_unique ? 1 : (parseInt(editedProduct.stock) || 0)));
      formData.append('is_unique', String(!!editedProduct.is_unique));
      formData.append('is_sold', String(!!editedProduct.is_sold));
      
      // Ajouter les relations (arrays)
      if (editedProduct.category) editedProduct.category.forEach(id => formData.append('category', id));
      if (editedProduct.color) editedProduct.color.forEach(id => formData.append('color', id));
      if (editedProduct.material) editedProduct.material.forEach(id => formData.append('material', id));
      if (editedProduct.pearl_type) editedProduct.pearl_type.forEach(id => formData.append('pearl_type', id));

      // Ajouter les images existantes (celles qui n'ont pas été supprimées)
      const currentImages = product.image || [];
      if (currentImages.length > 0) {
        currentImages.forEach(imgName => formData.append('image', imgName));
      } else {
        formData.append('image', ''); // Vide le champ image si aucune image existante
      }

      // Première mise à jour avec les métadonnées et images existantes
      let updatedRecord = await pb.collection('products').update(product.id, formData);

      // Si il y a de nouvelles images, les ajouter aux images existantes
      if (newImages.length > 0) {
        const addImagesFormData = new FormData();
        
        // D'abord, récupérer le produit mis à jour pour avoir les images actuelles
        const currentProduct = await pb.collection('products').getOne(product.id);
        const existingImages = currentProduct.image || [];
        
        // Ajouter toutes les images existantes
        existingImages.forEach(imgName => addImagesFormData.append('image', imgName));
        
        // Ajouter les nouvelles images
        newImages.forEach(file => addImagesFormData.append('image', file));
        
        updatedRecord = await pb.collection('products').update(product.id, addImagesFormData);
      }
      
      // Recharger le produit avec les relations
      const refreshedProduct = await pb.collection('products').getOne(product.id, {
        expand: 'category,color,material,pearl_type'
      });
      
      setProduct(refreshedProduct);
      setIsEditing(false);
      setEditedProduct(null);
      setNewImages([]);
      alert('Produit mis à jour avec succès !');
    } catch (error) {
      console.error('Erreur lors de la mise à jour:', error);
      alert('Erreur lors de la mise à jour du produit');
    }
  };

  const handleInputChange = (field, value) => {
    setEditedProduct(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Fonctions pour la gestion des images
  const handleFileChange = async (files) => {
    const fileArray = Array.from(files);
    
    // Compression des images
    const compressedFiles = await Promise.all(fileArray.map(async (file) => {
      const options = {
        maxSizeMB: 1,
        maxWidthOrHeight: 1920,
        useWebWorker: true,
        initialQuality: 0.8
      };
      try {
        if (file.size / 1024 / 1024 > options.maxSizeMB) {
          const compressedFile = await imageCompression(file, options);
          return compressedFile;
        } else {
          return file;
        }
      } catch (error) {
        console.error('Erreur de compression:', error);
        return file;
      }
    }));

    setNewImages(prev => [...prev, ...compressedFiles]);
  };

  const handleFileDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileChange(e.dataTransfer.files);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDeleteNewImage = (filenameToDelete) => {
    setNewImages(prev => prev.filter(file => file.name !== filenameToDelete));
  };

  const handleDeleteExistingImage = (filenameToDelete) => {
    setProduct(prev => {
      const updatedImages = prev.image.filter(img => img !== filenameToDelete);
      
      // Mettre à jour l'image active si l'image supprimée était l'image active
      if (updatedImages.length > 0) {
        const newActiveImageUrl = pb.files.getURL(prev, updatedImages[0]);
        setActiveImage(newActiveImageUrl);
      }
      
      return {
        ...prev,
        image: updatedImages
      };
    });
  };

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    setDragOverIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    
    if (draggedIndex === null || draggedIndex === dropIndex) return;

    const existingImages = product.image || [];
    const allImages = [...existingImages, ...newImages];
    
    // Réorganiser les images
    const draggedImage = allImages[draggedIndex];
    const newAllImages = [...allImages];
    newAllImages.splice(draggedIndex, 1);
    newAllImages.splice(dropIndex, 0, draggedImage);

    // Séparer les images existantes et nouvelles
    const newExistingImages = [];
    const newNewImages = [];

    newAllImages.forEach(img => {
      if (typeof img === 'string') {
        newExistingImages.push(img);
      } else {
        newNewImages.push(img);
      }
    });

    setProduct(prev => ({
      ...prev,
      image: newExistingImages
    }));
    setNewImages(newNewImages);
    
    // Mettre à jour l'image active si nécessaire
    if (newExistingImages.length > 0) {
      const newActiveImageUrl = pb.files.getURL(product, newExistingImages[0]);
      setActiveImage(newActiveImageUrl);
    }
    
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleSelectChange = (selectedOptions, actionMeta) => {
    const fieldName = actionMeta.name;
    const values = selectedOptions ? selectedOptions.map(option => option.value) : [];
    setEditedProduct(prev => ({
      ...prev,
      [fieldName]: values
    }));
  };

  // Simplification de l'objet produit pour le panier et les favoris
  const simplifiedProduct = {
    id: product.id,
    nom: product.name,
    prix: product.price,
    images: productImages
  };

  return (
    <>
      <div className="product-detail-page">
        <button onClick={() => navigate(-1)} className="back-btn">← Retour</button>
        <div className="product-detail-container">
          <div className="product-gallery">
            {isEditing ? (
              <div className="image-management-section">
                <h3>Gestion des images</h3>
                
                {/* Zone de drop pour nouvelles images */}
                <div 
                  className={`image-drop-zone ${isDragging ? 'dragging' : ''}`}
                  onDrop={handleFileDrop}
                  onDragOver={handleDrag}
                  onDragEnter={handleDragEnter}
                  onDragLeave={handleDragLeave}
                >
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={(e) => handleFileChange(e.target.files)}
                    className="file-input"
                    id="image-upload"
                  />
                  <label htmlFor="image-upload" className="drop-label">
                    <svg viewBox="0 0 24 24" className="upload-icon">
                      <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
                    </svg>
                    <span>Glissez vos images ici ou cliquez pour sélectionner</span>
                  </label>
                </div>

                {/* Images existantes et nouvelles */}
                <div className="images-grid">
                  {/* Images existantes */}
                  {product.image && product.image.map((img, index) => {
                    const allImagesIndex = index;
                    return (
                      <div 
                        key={`existing-${index}`} 
                        className={`image-item existing-image ${draggedIndex === allImagesIndex ? 'dragging' : ''} ${dragOverIndex === allImagesIndex ? 'drag-over' : ''}`}
                        draggable
                        onDragStart={(e) => handleDragStart(e, allImagesIndex)}
                        onDragOver={(e) => handleDragOver(e, allImagesIndex)}
                        onDragEnd={handleDragEnd}
                        onDrop={(e) => handleDrop(e, allImagesIndex)}
                      >
                        <img src={pb.files.getURL(product, img)} alt={`Image ${index + 1}`} />
                        <button 
                          type="button" 
                          onClick={() => handleDeleteExistingImage(img)}
                          className="delete-image-btn"
                        >
                          ×
                        </button>
                        <span className="image-order">{index + 1}</span>
                      </div>
                    );
                  })}
                  
                  {/* Nouvelles images */}
                  {newImages.map((file, index) => {
                    const allImagesIndex = (product.image?.length || 0) + index;
                    return (
                      <div 
                        key={`new-${index}`} 
                        className={`image-item new-image ${draggedIndex === allImagesIndex ? 'dragging' : ''} ${dragOverIndex === allImagesIndex ? 'drag-over' : ''}`}
                        draggable
                        onDragStart={(e) => handleDragStart(e, allImagesIndex)}
                        onDragOver={(e) => handleDragOver(e, allImagesIndex)}
                        onDragEnd={handleDragEnd}
                        onDrop={(e) => handleDrop(e, allImagesIndex)}
                      >
                        <img src={URL.createObjectURL(file)} alt={`Nouvelle image ${index + 1}`} />
                        <button 
                          type="button" 
                          onClick={() => handleDeleteNewImage(file.name)}
                          className="delete-image-btn"
                        >
                          ×
                        </button>
                        <span className="image-order">{allImagesIndex + 1}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <>
                <div className="main-image-container" onClick={() => handleImageClick(0)}>
                  <img src={activeImage} alt={product.name} className="main-image" />
                  <div className="zoom-overlay">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
                    </svg>
                  </div>
                </div>
                <div className="thumbnail-gallery">
                  {productImages.map((image, index) => (
                    <button key={index} className={`thumbnail-btn ${image.src === activeImage ? 'active' : ''}`} onClick={() => setActiveImage(image.src)}>
                      <img src={image.src} alt={`Miniature ${product.name} ${index + 1}`} className="thumbnail-image" />
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
          <div className="product-detail-info">
            {isEditing ? (
              <input 
                type="text" 
                value={editedProduct.name} 
                onChange={(e) => handleInputChange('name', e.target.value)}
                className="edit-input edit-title"
              />
            ) : (
              <h1>{product.name}</h1>
            )}
            {isEditing ? (
              <input 
                type="number" 
                value={editedProduct.price} 
                onChange={(e) => handleInputChange('price', parseFloat(e.target.value))}
                className="edit-input edit-price"
                step="0.01"
              />
            ) : (
              <p className="price">{product.price} €</p>
            )}
            
            <div className="product-stock-detail">
              {isEditing ? (
                <div className="edit-stock-controls">
                  <label>
                    <input 
                      type="checkbox" 
                      checked={editedProduct.is_unique}
                      onChange={(e) => handleInputChange('is_unique', e.target.checked)}
                    />
                    Pièce unique
                  </label>
                  <label>
                    <input 
                      type="checkbox" 
                      checked={editedProduct.is_sold}
                      onChange={(e) => handleInputChange('is_sold', e.target.checked)}
                    />
                    Vendu
                  </label>
                  {!editedProduct.is_unique && (
                    <label>
                      Stock: 
                      <input 
                        type="number" 
                        value={editedProduct.stock} 
                        onChange={(e) => handleInputChange('stock', parseInt(e.target.value))}
                        className="edit-input edit-stock"
                        min="0"
                      />
                    </label>
                  )}
                </div>
              ) : (
                <>
                  {product.is_unique ? (
                    <span className="unique-item">Pièce unique</span>
                  ) : product.stock > 0 ? (
                    <span className="in-stock">En stock: {product.stock} disponible{product.stock > 1 ? 's' : ''}</span>
                  ) : (
                    <span className="out-of-stock">Rupture de stock</span>
                  )}
                </>
              )}
            </div>
            {isEditing ? (
              <textarea 
                value={editedProduct.description} 
                onChange={(e) => handleInputChange('description', e.target.value)}
                className="edit-input edit-description"
                rows="4"
              />
            ) : (
              <p className="description">{product.description}</p>
            )}
            
            {isEditing ? (
              <div className="edit-selects-container">
                <div className="edit-select-group">
                  <label>Catégories</label>
                  <Select
                    name="category"
                    options={categories.map(c => ({ value: c.id, label: c.name }))}
                    isMulti
                    value={categories.filter(c => editedProduct.category.includes(c.id)).map(c => ({ value: c.id, label: c.name }))}
                    onChange={handleSelectChange}
                    placeholder="Sélectionner des catégories"
                    className="edit-select"
                  />
                </div>
                
                <div className="edit-select-group">
                  <label>Couleurs</label>
                  <Select
                    name="color"
                    options={colors.map(c => ({ value: c.id, label: c.name }))}
                    isMulti
                    value={colors.filter(c => editedProduct.color.includes(c.id)).map(c => ({ value: c.id, label: c.name }))}
                    onChange={handleSelectChange}
                    placeholder="Sélectionner des couleurs"
                    className="edit-select"
                  />
                </div>
                
                <div className="edit-select-group">
                  <label>Matériaux</label>
                  <Select
                    name="material"
                    options={materials.map(m => ({ value: m.id, label: m.name }))}
                    isMulti
                    value={materials.filter(m => editedProduct.material.includes(m.id)).map(m => ({ value: m.id, label: m.name }))}
                    onChange={handleSelectChange}
                    placeholder="Sélectionner des matériaux"
                    className="edit-select"
                  />
                </div>
                
                <div className="edit-select-group">
                  <label>Types de perles</label>
                  <Select
                    name="pearl_type"
                    options={pearlTypes.map(p => ({ value: p.id, label: p.name }))}
                    isMulti
                    value={pearlTypes.filter(p => editedProduct.pearl_type.includes(p.id)).map(p => ({ value: p.id, label: p.name }))}
                    onChange={handleSelectChange}
                    placeholder="Sélectionner des types de perles"
                    className="edit-select"
                  />
                </div>
              </div>
            ) : (
              <div className="product-meta">
                {product.expand?.category && <span><strong>Catégorie:</strong> {product.expand.category.map(c => c.name).join(', ')}</span>}
                {product.expand?.material && <span><strong>Matière:</strong> {product.expand.material.map(m => m.name).join(', ')}</span>}
                {product.expand?.pearl_type && <span><strong>Perle:</strong> {product.expand.pearl_type.map(p => p.name).join(', ')}</span>}
                {product.expand?.color && <span><strong>Couleur:</strong> {product.expand.color.map(c => c.name).join(', ')}</span>}
              </div>
            )}
            
            {/* Boutons d'édition admin repositionnés */}
            {isAdmin && (
              <div className="admin-controls-repositioned">
                {!isEditing ? (
                  <button className="edit-btn" onClick={startEditing}>
                    ✏️ Modifier le produit
                  </button>
                ) : (
                  <div className="edit-controls">
                    <button className="save-btn" onClick={saveProduct}>
                      ✅ Sauvegarder
                    </button>
                    <button className="cancel-btn" onClick={cancelEditing}>
                      ❌ Annuler
                    </button>
                  </div>
                )}
              </div>
            )}
            
            <div className="product-actions">
              <button className="add-to-cart-btn" onClick={() => addToCart(simplifiedProduct)}>Ajouter au panier</button>
              <button className={`add-to-favorites-btn-detail ${isFavorited ? 'favorited' : ''}`} onClick={() => toggleFavorites(simplifiedProduct)}>
                <svg viewBox="0 0 24 24"><path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/></svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      {similarProducts.length > 0 && (
        <section className="similar-products">
          <h2>Vous aimerez aussi...</h2>
          <div className="slider-container">
            <div className="product-slider">
              {/* Dupliquer les produits pour un défilement infini */}
              {[...similarProducts, ...similarProducts].map((similarProduct, index) => {
                const isSimilarFavorited = favorites.some(item => item.id === similarProduct.id);
                const similarProductImage = similarProduct.image && similarProduct.image.length > 0 ? pb.files.getURL(similarProduct, similarProduct.image[0]) : '/placeholder.jpg';
                const simplifiedSimilarProduct = { id: similarProduct.id, nom: similarProduct.name, prix: similarProduct.price, images: [{ src: similarProductImage }] };

                return (
                  <Link to={`/catalogue/produit/${similarProduct.slug}`} key={`${similarProduct.id}-${index}`} className="product-card-link slider-item">
                    <div className="product-card">
                      <div className="product-image-wrapper">
                        <img src={similarProductImage} alt={similarProduct.name} className="product-image" />
                        <button className={`add-to-favorites-btn ${isSimilarFavorited ? 'favorited' : ''}`} onClick={(e) => { e.preventDefault(); toggleFavorites(simplifiedSimilarProduct); }} aria-label="Ajouter aux favoris">
                          <svg viewBox="0 0 24 24"><path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/></svg>
                        </button>
                      </div>
                      <div className="product-info">
                        <h3 className="product-name">{similarProduct.name}</h3>
                        <p className="product-price">
                          {similarProduct.is_sold ? 'Vendu' : `${similarProduct.price} €`}
                        </p>
                        <div className={`product-stock ${similarProduct.is_unique ? 'unique' : (similarProduct.stock > 0 ? '' : 'out-of-stock')}`}>
                          {similarProduct.is_unique ? 'Pièce unique' : (similarProduct.stock > 0 ? `Stock: ${similarProduct.stock}` : 'Rupture de stock')}
                        </div>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      <Lightbox open={openLightbox} close={() => setOpenLightbox(false)} slides={productImages} index={imageIndex} />
    </>
  );
}

export default ProductDetail;